import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { supabase } from '../../api/initSupabase';
import { logErrorsWithMessage } from '../../utils/errors';
import { localAnalytics } from '../../utils/analytics';
import { Loading } from '../common/Loading';
import { PaywallModal } from '../PaywallModal';
import { BookInProgressModal } from '../BookInProgressModal';
import { fbTracking } from '../../utils/fbTracking';
import {
  Header,
  BookDisplay,
  ChapterContent,
  ChapterBottomMenu,
  ChapterNavigation,
  ChapterList,
} from '.';
import { SEOHead } from '../common';
import { generateChapterSEO } from '../../utils/seoUtils';
import { usePurchases } from '../../contexts/PurchasesContext';

interface ChapterType {
  id: number;
  book_id: number;
  number: number;
  title: string;
  is_premium: boolean;
  is_finished: boolean;
  coins: number;
  content?: string;
  preview?: string;
}

interface BookType {
  id: number;
  chapter_count: number;
  read_count: number;
  title: string;
  picture: string | null;
  author: string;
  slug: string;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords: string[];
  seo_alt?: string | null;
}

export const ChapterAuthenticated: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { bookSlug, chapterNumber } = useParams<{
    bookSlug: string;
    chapterNumber: string;
  }>();
  const { userId, isCampaign, isAnonymous } = useAuth();
  const { isUserPremium } = usePurchases();

  const [loading, setLoading] = useState(true);
  const [chapter, setChapter] = useState<ChapterType | null>(null);
  const [book, setBook] = useState<BookType | null>(null);
  const [chapterContent, setChapterContent] = useState<string | null>(null);
  const [userCoins, setUserCoins] = useState(0);
  const [showUnlockPrompt, setShowUnlockPrompt] = useState(false);
  const [showChapterList, setShowChapterList] = useState(false);
  const [autoUnlockEnabled, setAutoUnlockEnabled] = useState(false);
  const [allChapters, setAllChapters] = useState<ChapterType[]>([]);
  const [showPaywall, setShowPaywall] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [fontSize, setFontSize] = useState(18);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showInProgressModal, setShowInProgressModal] = useState(false);

  React.useEffect(() => {
    const savedPreference = localStorage.getItem('autoUnlockCoins');
    setAutoUnlockEnabled(savedPreference === 'true');

    const savedFontSize = localStorage.getItem('chapterFontSize');
    if (savedFontSize) {
      setFontSize(parseInt(savedFontSize, 10));
    }
  }, []);

  const handleAutoUnlockToggle = () => {
    const newValue = !autoUnlockEnabled;
    setAutoUnlockEnabled(newValue);
    localStorage.setItem('autoUnlockCoins', newValue.toString());

    if (userId) {
      supabase
        .from('user_technical_details')
        .update({
          auto_unlock_chapters: newValue,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId)
        .then(({ error }) => {
          if (error) {
            console.error('Error updating auto-unlock preference:', error);
          }
        });
    }

    localAnalytics().logEvent('AutoUnlockToggled', {
      screen: 'ChapterAuthenticated',
      action: 'AutoUnlockToggled',
      userId,
      enabled: newValue,
    });
  };

  const handleFontSizeChange = (newSize: number) => {
    setFontSize(newSize);
    localStorage.setItem('chapterFontSize', newSize.toString());

    localAnalytics().logEvent('FontSizeChanged', {
      screen: 'ChapterAuthenticated',
      action: 'FontSizeChanged',
      userId,
      fontSize: newSize,
    });
  };

  const handleAddToFavorites = async () => {
    if (!book?.id) return;

    try {
      if (isFavorite) {
        await supabase
          .from('book_favorite')
          .delete()
          .eq('user_id', userId!)
          .eq('book_id', book.id);
        setIsFavorite(false);
      } else {
        await supabase.from('book_favorite').insert([
          {
            user_id: userId!,
            book_id: book.id,
          },
        ]);
        setIsFavorite(true);
      }

      localAnalytics().logEvent('BookFavoriteToggled', {
        screen: 'ChapterAuthenticated',
        action: 'FavoriteToggled',
        userId,
        bookSlug,
        isFavorite: !isFavorite,
      });
    } catch (error) {
      logErrorsWithMessage(error, t('failed_to_toggle_favorite'));
    }
  };

  const fetchChapterData = async () => {
    if (!bookSlug || !chapterNumber) return;

    setLoading(true);

    try {
      localAnalytics().logEvent('ChapterDataLoading', {
        screen: 'ChapterAuthenticated',
        action: 'DataLoading',
        userId,
        bookSlug,
        chapterNumber,
      });

      const results = await Promise.all([
        supabase
          .from('book')
          .select(
            'id, chapter_count, title, picture, slug, read_count, seo_title, seo_description, seo_keywords, seo_alt, book_author!inner(author:author_id(name))'
          )
          .eq('slug', bookSlug)
          .single(),
        supabase
          .from('chapter')
          .select(
            `
            *,
            chapter_content (content),
            chapter_user (chapter_id),
            book!inner(slug)
          `
          )
          .eq('book.slug', bookSlug)
          .eq('number', parseInt(chapterNumber))
          .maybeSingle(),
        supabase
          .from('chapter')
          .select('*, book!inner(slug)')
          .eq('book.slug', bookSlug)
          .order('number'),
        supabase
          .from('user_premium')
          .select('coins')
          .eq('user_id', userId!)
          .single(),
        supabase
          .from('user_technical_details')
          .select('auto_unlock_chapters')
          .eq('user_id', userId!)
          .single(),
      ]);

      const [
        bookResult,
        chapterResult,
        allChaptersResult,
        userPremiumResult,
        userTechnicalResult,
      ] = results;

      if (bookResult.error) throw bookResult.error;
      if (chapterResult.error) throw chapterResult.error;

      const favoriteResult = await supabase
        .from('book_favorite')
        .select('*')
        .eq('user_id', userId!)
        .eq('book_id', bookResult.data.id)
        .maybeSingle();

      setBook({
        ...bookResult.data,
        author: bookResult.data.book_author[0].author.name,
      });
      setChapter(chapterResult.data);
      setAllChapters(allChaptersResult.data || []);
      setUserCoins(userPremiumResult?.data?.coins || 0);
      setAutoUnlockEnabled(
        userTechnicalResult?.data?.auto_unlock_chapters ||
          localStorage.getItem('autoUnlockCoins') === 'true'
      );
      setIsFavorite(!!favoriteResult.data);

      if (bookResult.data && chapterResult.data) {
        fbTracking.trackViewContent(
          `${bookResult.data.title} - Chapter ${chapterNumber}`
        );
      }

      if (chapterResult.data) {
        const content = chapterResult.data.chapter_content?.[0]?.content;
        const hasChapterUser =
          chapterResult.data.chapter_user &&
          chapterResult.data.chapter_user.length > 0;

        if (!chapterResult.data.is_finished) {
          setShowInProgressModal(true);
          return;
        }

        if (content) {
          setChapterContent(content);

          if (!chapterResult.data.is_premium && !hasChapterUser) {
            void unlockChapter(chapterResult.data.id).catch(error => {
              console.error('Background chapter activation failed:', error);
            });
          }
        } else if (
          !chapterResult.data.is_premium ||
          isUserPremium ||
          (autoUnlockEnabled &&
            (userPremiumResult?.data?.coins || 0) >= chapterResult.data.coins)
        ) {
          await unlockChapter(chapterResult.data.id);
        } else {
          setChapterContent('');
          setShowUnlockPrompt(true);
        }
      } else if (
        bookResult.data &&
        parseInt(chapterNumber || '1') <= bookResult.data.chapter_count
      ) {
        setShowInProgressModal(true);
        return;
      }

      localAnalytics().logEvent('ChapterDataLoaded', {
        screen: 'ChapterAuthenticated',
        action: 'DataLoaded',
        userId,
        bookSlug,
        chapterNumber,
        isCampaign,
        hasContent: !!chapterResult.data?.chapter_content?.[0]?.content,
      });
    } catch (error) {
      logErrorsWithMessage(error, t('failed_to_load_chapter'));
    } finally {
      setLoading(false);
    }
  };

  const unlockChapter = async (chapterId: number) => {
    try {
      localAnalytics().logEvent('ChapterUnlockAttempt', {
        screen: 'ChapterAuthenticated',
        action: 'UnlockAttempt',
        userId,
        chapterId,
      });

      const { error } = await supabase.rpc('activate_chapter', {
        p_chapter_id: chapterId,
      });

      if (error) throw error;

      const { data: contentData } = await supabase
        .from('chapter_content')
        .select('content')
        .eq('chapter_id', chapterId)
        .single();

      if (contentData) {
        setChapterContent(contentData.content);
        setShowUnlockPrompt(false);

        const { data: userPremiumData } = await supabase
          .from('user_premium')
          .select('coins')
          .eq('user_id', userId!)
          .single();

        if (userPremiumData) {
          setUserCoins(userPremiumData.coins);
        }

        localAnalytics().logEvent('ChapterUnlockSuccess', {
          screen: 'ChapterAuthenticated',
          action: 'UnlockSuccess',
          userId,
          chapterId,
          hasContent: !!contentData.content,
        });
      }
    } catch (error) {
      logErrorsWithMessage(error, t('failed_to_unlock_chapter'));
    }
  };

  const handlePurchaseSuccess = async () => {
    setShowPaywall(false);

    const { data: userPremiumData } = await supabase
      .from('user_premium')
      .select('coins')
      .eq('user_id', userId!)
      .single();

    if (userPremiumData) {
      setUserCoins(userPremiumData.coins);
    }

    if (chapter) {
      await unlockChapter(chapter.id);
    }
  };

  const handleBuyCoinsClick = () => {
    localAnalytics().logEvent('ChapterBuyCoinsClicked', {
      screen: 'ChapterAuthenticated',
      action: 'BuyCoinsClicked',
      userId,
      bookSlug,
      chapterNumber,
      bookTitle: book?.title,
    });

    if (book) {
      fbTracking.trackAddToCart(book.title);
    }

    setShowPaywall(true);
  };

  const handleNextChapter = () => {
    if (!book || !chapterNumber) return;

    const nextChapter = parseInt(chapterNumber) + 1;

    if (nextChapter <= book.chapter_count) {
      localAnalytics().logEvent('ChapterNextClicked', {
        screen: 'ChapterAuthenticated',
        action: 'NextClicked',
        userId,
        currentChapter: chapterNumber,
        nextChapter,
      });

      fbTracking.trackAddToWishlist(book.title);
    }
  };

  const handleChapterSelect = (selectedChapterNumber: number) => {
    localAnalytics().logEvent('ChapterSelected', {
      screen: 'ChapterAuthenticated',
      action: 'ChapterSelected',
      userId,
      currentChapter: chapterNumber,
      selectedChapter: selectedChapterNumber,
    });

    setShowChapterList(false);
  };

  useEffect(() => {
    fetchChapterData();
  }, [bookSlug, chapterNumber]);

  useEffect(() => {
    if (!loading && (!book || (!chapter && !showInProgressModal))) {
      localAnalytics().logEvent('ChapterNotFoundRedirect', {
        screen: 'ChapterAuthenticated',
        action: 'RedirectTo404',
        userId,
        bookSlug,
        chapterNumber,
        reason: !book ? 'book_not_found' : 'chapter_not_found',
      });
      navigate('/404', { replace: true });
    }
  }, [
    navigate,
    userId,
    bookSlug,
    chapterNumber,
    book,
    chapter,
    showInProgressModal,
    loading,
  ]);

  if (loading) {
    return <Loading />;
  }

  if (!book || (!chapter && !showInProgressModal)) {
    return null;
  }

  const hasNextChapter = parseInt(chapterNumber || '1') < book.chapter_count;
  const hasPreviousChapter = parseInt(chapterNumber || '1') > 1;

  const seoData =
    book && chapter
      ? generateChapterSEO(
          book,
          {
            number: parseInt(chapterNumber || '1'),
            title: chapter.title || '',
            preview: chapter.preview,
          },
          t
        )
      : null;

  const styles: Record<string, React.CSSProperties> = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      display: 'flex',
      flexDirection: 'column',
    },
    main: {
      flex: 1,
      overflowY: 'auto',
    },
  };

  return (
    <>
      {seoData && (
        <SEOHead
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          canonical={seoData.canonical}
          ogImage={seoData.ogImage}
          ogType={seoData.ogType}
        />
      )}
      <div style={styles.container}>
        <Header
          isPremium={isUserPremium}
          isAuthenticated={true}
          isCampaign={isCampaign}
          isAnonymous={isAnonymous}
          userCoins={userCoins}
          menuOpen={menuOpen}
          onMenuToggle={() => setMenuOpen(!menuOpen)}
          onCoinsClick={() => setShowPaywall(true)}
        />

        <main style={styles.main}>
          <BookDisplay
            book={book}
            chapter={{
              number: parseInt(chapterNumber || '1'),
              title: chapter?.title || '',
            }}
            readers={book.read_count}
            showStats={!isCampaign}
          />

          {showInProgressModal ? (
            <BookInProgressModal
              isOpen={showInProgressModal}
              bookId={book.id}
              bookTitle={book.title}
              bookSlug={book.slug}
              currentChapter={parseInt(chapterNumber || '1')}
              totalChapters={book.chapter_count}
              onClose={() => setShowInProgressModal(false)}
            />
          ) : (
            <>
              <ChapterContent
                content={chapterContent}
                preview={chapter?.preview}
                isLocked={!chapterContent}
                isAuthenticated={true}
                showUnlockPrompt={showUnlockPrompt}
                userCoins={userCoins}
                chapterCoins={chapter?.coins}
                autoUnlockEnabled={autoUnlockEnabled}
                onUnlock={() => unlockChapter(chapter?.id || 0)}
                onBuyCoins={handleBuyCoinsClick}
                onAutoUnlockToggle={handleAutoUnlockToggle}
                fontSize={fontSize}
                chapter={{
                  number: parseInt(chapterNumber || '1'),
                  title: chapter?.title || '',
                }}
              />

              {chapterContent && (
                <ChapterNavigation
                  hasNextChapter={hasNextChapter}
                  hasPreviousChapter={hasPreviousChapter}
                  currentChapter={parseInt(chapterNumber || '1')}
                  totalChapters={book.chapter_count}
                  bookSlug={bookSlug!}
                  onNextChapter={handleNextChapter}
                />
              )}
            </>
          )}
        </main>

        <ChapterBottomMenu
          isAuthenticated={true}
          isCampaign={isCampaign}
          isAnonymous={isAnonymous}
          currentChapter={parseInt(chapterNumber || '1')}
          totalChapters={book.chapter_count}
          fontSize={fontSize}
          onShowChapterList={() => setShowChapterList(true)}
          onFontSizeChange={handleFontSizeChange}
          onAddToFavorites={handleAddToFavorites}
          isFavorite={isFavorite}
        />

        <ChapterList
          isOpen={showChapterList}
          chapters={allChapters}
          currentChapter={parseInt(chapterNumber || '1')}
          bookTitle={book.title}
          bookSlug={bookSlug!}
          onClose={() => setShowChapterList(false)}
          onChapterSelect={handleChapterSelect}
        />

        {showPaywall && book && (
          <PaywallModal
            isOpen={showPaywall}
            onClose={() => setShowPaywall(false)}
            onPurchaseSuccess={handlePurchaseSuccess}
            bookTitle={book.title}
            chapterNumber={parseInt(chapterNumber || '1')}
          />
        )}
      </div>
    </>
  );
};
